import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { NAV_BADGES_KEY } from "@/hooks/useNavBadges";
import type {
  AronPlannerOccurrence,
  AronPlannerOptions,
  AronPlannerTimeline,
  AronPlannerTimelineMeta,
  AronSchedulePreview,
  AronScheduledTask,
  AronScheduledTaskInput,
  AronScheduledTaskRun,
  AronScopeValidation,
  AronWriteScopeEntry,
} from "../types";

export const aronTaskKeys = {
  list: ["aron", "scheduled-tasks"] as const,
  runs: (taskId: string) => ["aron", "scheduled-tasks", taskId, "runs"] as const,
  upcoming: (days: number) => ["aron", "scheduled-tasks", "upcoming", days] as const,
  options: ["aron", "scheduled-tasks", "planner-options"] as const,
};

export function useAronScheduledTasks() {
  return useQuery({
    queryKey: aronTaskKeys.list,
    queryFn: async () => {
      const r = await apiClient.get<{ data: AronScheduledTask[] }>(
        "/aron/scheduled-tasks/",
        { params: { page_size: 200 } },
      );
      return r.data.data;
    },
    staleTime: 15_000,
  });
}

export function useAronScheduledTaskRuns(taskId: string | null) {
  return useQuery({
    queryKey: taskId ? aronTaskKeys.runs(taskId) : ["aron", "scheduled-tasks", "noop"],
    enabled: !!taskId,
    queryFn: async () => {
      const r = await apiClient.get<{ data: AronScheduledTaskRun[] }>(
        `/aron/scheduled-tasks/${taskId}/runs/`,
      );
      return r.data.data;
    },
  });
}

/** The planner axis: every fire time of the visible tasks within the horizon. */
export function useAronUpcomingRuns(days: number) {
  return useQuery({
    queryKey: aronTaskKeys.upcoming(days),
    queryFn: async () => {
      const r = await apiClient.get<{
        data: AronPlannerOccurrence[];
        meta: AronPlannerTimelineMeta;
      }>("/aron/scheduled-tasks/upcoming/", { params: { days } });
      return { occurrences: r.data.data, meta: r.data.meta } as AronPlannerTimeline;
    },
    staleTime: 30_000,
  });
}

/** Server-side authority for the form: dispatcher flag, floors, caps, sharing rights. */
export function useAronPlannerOptions() {
  return useQuery({
    queryKey: aronTaskKeys.options,
    queryFn: async () => {
      const r = await apiClient.get<{ data: AronPlannerOptions }>(
        "/aron/scheduled-tasks/planner-options/",
      );
      return r.data.data;
    },
    staleTime: 5 * 60_000,
  });
}

/** Live feedback for the schedule picker — never guessed in the browser: the same
 *  cron parser that will run the task answers, including the frequency floor. */
export function usePreviewAronSchedule() {
  return useMutation({
    mutationFn: async (cron: string) => {
      const r = await apiClient.post<{ data: AronSchedulePreview }>(
        "/aron/scheduled-tasks/preview-schedule/",
        { cron_expression: cron },
      );
      return r.data.data;
    },
  });
}

/** Dry-run of a write scope: the same fail-closed catalogue check plus Polish wording. */
export function useValidateAronWriteScope() {
  return useMutation({
    mutationFn: async (scope: AronWriteScopeEntry[]) => {
      const r = await apiClient.post<{ data: AronScopeValidation }>(
        "/aron/scheduled-tasks/validate-scope/",
        { write_scope: scope },
      );
      return r.data.data;
    },
  });
}

export function useCreateAronScheduledTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AronScheduledTaskInput) => {
      const r = await apiClient.post<{ data: AronScheduledTask }>(
        "/aron/scheduled-tasks/",
        input,
      );
      return r.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: aronTaskKeys.list });
      qc.invalidateQueries({ queryKey: ["aron", "scheduled-tasks", "upcoming"] });
    },
  });
}

export interface UpdateScheduledTaskVars {
  id: string;
  patch: AronScheduledTaskInput;
}

export function useUpdateAronScheduledTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: UpdateScheduledTaskVars) => {
      const r = await apiClient.patch<{ data: AronScheduledTask }>(
        `/aron/scheduled-tasks/${id}/`,
        patch,
      );
      return r.data.data;
    },
    onSuccess: (task) => {
      qc.setQueryData<AronScheduledTask[]>(aronTaskKeys.list, (prev) =>
        prev?.map((t) => (t.id === task.id ? task : t)),
      );
      // The axis is derived from cron + approval state, both of which an edit can move.
      qc.invalidateQueries({ queryKey: ["aron", "scheduled-tasks", "upcoming"] });
    },
  });
}

export function useDeleteAronScheduledTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/aron/scheduled-tasks/${id}/`);
      return id;
    },
    onSuccess: (id) => {
      qc.setQueryData<AronScheduledTask[]>(aronTaskKeys.list, (prev) =>
        prev?.filter((t) => t.id !== id),
      );
      qc.invalidateQueries({ queryKey: ["aron", "scheduled-tasks", "upcoming"] });
    },
  });
}

/** Clear the "new result" badge — every finished run of the task counts as read. */
export function useMarkAronScheduledTaskRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await apiClient.post<{ data: AronScheduledTask }>(
        `/aron/scheduled-tasks/${id}/mark-read/`,
      );
      return r.data.data;
    },
    onSuccess: (task) => {
      qc.setQueryData<AronScheduledTask[]>(aronTaskKeys.list, (prev) =>
        prev?.map((t) => (t.id === task.id ? task : t)),
      );
      qc.invalidateQueries({ queryKey: aronTaskKeys.runs(task.id) });
      // The same count drives the sidebar badge — clear it in the same breath.
      qc.invalidateQueries({ queryKey: NAV_BADGES_KEY });
    },
  });
}

export function useRunAronScheduledTaskNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.post(`/aron/scheduled-tasks/${id}/run-now/`);
      return id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: aronTaskKeys.list });
      qc.invalidateQueries({ queryKey: aronTaskKeys.runs(id) });
    },
  });
}

export function useApproveAronScheduledTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await apiClient.post<{ data: AronScheduledTask }>(
        `/aron/scheduled-tasks/${id}/approve/`,
      );
      return r.data.data;
    },
    onSuccess: (task) => {
      qc.setQueryData<AronScheduledTask[]>(aronTaskKeys.list, (prev) =>
        prev?.map((t) => (t.id === task.id ? task : t)),
      );
    },
  });
}

export function useRejectAronScheduledTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const r = await apiClient.post<{ data: AronScheduledTask }>(
        `/aron/scheduled-tasks/${id}/reject/`,
        { reason },
      );
      return r.data.data;
    },
    onSuccess: (task) => {
      qc.setQueryData<AronScheduledTask[]>(aronTaskKeys.list, (prev) =>
        prev?.map((t) => (t.id === task.id ? task : t)),
      );
    },
  });
}
